import React, { useEffect, useState } from 'react';
import { getActivityImpactOnMoodAndIL, CorrelationResult } from '@/utils/wellnessCorrelations';
import { TrendingUp, Loader2 } from 'lucide-react';

// Activity types that the correlation helper can analyse
const KNOWN_ACTIVITY_TYPES = [
  'meditation',
  'breathwork',
  'eft/tapping',
  'nature_time',
  'exercise',
  'sauna',
  'cold_plunge',
  'journaling',
  'gratitude',
  'visualisation',
  'nervous_system_reset'
];

// Map challenge categories/metric_keys to correlation activity types
const CATEGORY_TO_ACTIVITY: Record<string, string> = {
  meditation: 'meditation',
  movement: 'exercise',
  stress: 'breathwork',
};

interface ChallengeCorrelationsPanelProps {
  userId: string;
  category: string;
  metricSource: string;
  metricKey?: string | null;
}

export function ChallengeCorrelationsPanel({
  userId,
  category,
  metricSource,
  metricKey
}: ChallengeCorrelationsPanelProps) {
  const [loading, setLoading] = useState(true);
  const [correlation, setCorrelation] = useState<CorrelationResult | null>(null);
  const [activityType, setActivityType] = useState<string | null>(null);

  useEffect(() => {
    const fetchCorrelation = async () => {
      setLoading(true);
      setCorrelation(null);

      // Determine which activity type to look up
      let targetActivity: string | null = null;

      // If metric_source is 'wellness_activities' and metric_key is a known type
      if (metricSource === 'wellness_activities' && metricKey) {
        const normalizedKey = metricKey.toLowerCase().replace(/\s+/g, '_');
        if (KNOWN_ACTIVITY_TYPES.includes(normalizedKey)) {
          targetActivity = normalizedKey;
        }
      }

      // Fallback: try mapping from category
      if (!targetActivity && CATEGORY_TO_ACTIVITY[category]) {
        targetActivity = CATEGORY_TO_ACTIVITY[category];
      }

      setActivityType(targetActivity);

      if (!targetActivity) {
        setLoading(false);
        return;
      }

      try {
        const result = await getActivityImpactOnMoodAndIL(userId, targetActivity, 60);
        setCorrelation(result);
      } catch (err) {
        console.error('Error fetching correlation for challenge', err);
      }

      setLoading(false);
    };

    fetchCorrelation();
  }, [userId, category, metricSource, metricKey]);

  // Don't show anything if we can't map this challenge to an activity type
  if (!loading && !activityType) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Checking your data...</span>
      </div>
    );
  }

  const hasInterpretation = correlation && correlation.interpretation && correlation.interpretation.trim().length > 0;

  return (
    <div className="mt-3 pt-3 border-t border-border/50">
      <div className="flex items-start gap-2">
        <TrendingUp className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          <p className="text-xs font-medium text-foreground">
            What your data suggests so far…
          </p>
          {hasInterpretation ? (
            <p className="text-xs text-muted-foreground leading-relaxed">
              {correlation.interpretation}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Therappi doesn't have enough data yet to see a clear pattern for this challenge. 
              As you keep logging and checking in, we'll reflect back any shifts we notice.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChallengeCorrelationsPanel;
